import React, { useMemo, useState } from 'react'
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import {
  ClockIcon,
  EyeIcon,
  GripVerticalIcon,
  MapPinIcon,
  NavigationIcon,
  UserIcon,
  UsersIcon,
} from 'lucide-react'
import {
  DayJobAssignPanel,
  type DayAssignCrew,
  type DayAssignEmployee,
  type DayAssignRow,
} from './DayJobAssignPanel'
import { jobHasAssigneeCoverage } from '../../lib/jobAssignmentHistory'
import { formatEmployeeLabel } from '../../lib/formatEmployeeLabel'

export type DispatchJob = {
  id: string
  job_number: string
  title: string
  status: string
  scheduled_start_time?: string | null
  scheduled_end_time?: string | null
  customerLabel: string
  propertyLabel: string
  assigned_crew_id?: string | null
  assigned_employee_id?: string | null
}

type Props = {
  jobs: DispatchJob[]
  jobAssignments: Record<string, DayAssignRow[]>
  crews: DayAssignCrew[]
  employees: DayAssignEmployee[]
  crewJobCounts: Record<string, number>
  employeeJobCounts: Record<string, number>
  assigningJobId: string | null
  savingAssignment: boolean
  getStatusColor: (status: string) => string
  formatTime: (time: string) => string
  onToggleEdit: (jobId: string) => void
  onAssignCrew: (jobId: string, crewId: string) => void
  onAssignEmployee: (jobId: string, employeeId: string) => void
  onRemove: (assignmentId: string, jobId: string) => void
  onViewJob: (jobId: string) => void
  onAutoAssignByProximity?: () => void
  autoAssigning?: boolean
}

type DragPayload =
  | { type: 'employee'; id: string; label: string }
  | { type: 'crew'; id: string; label: string }

function workloadBar(count: number) {
  const filled = Math.min(count, 5)
  return (
    <span className="inline-flex gap-0.5" aria-hidden>
      {Array.from({ length: 5 }).map((_, i) => (
        <span
          key={i}
          className={`w-1.5 h-2 rounded-sm ${
            i < filled
              ? count >= 3
                ? 'bg-amber-500'
                : 'bg-blue-500'
              : 'bg-gray-200'
          }`}
        />
      ))}
    </span>
  )
}

function capacityBadge(n: number) {
  if (n === 0) {
    return (
      <span className="flex-shrink-0 px-1.5 py-0.5 rounded font-medium bg-green-50 text-green-700">
        Free
      </span>
    )
  }
  if (n >= 3) {
    return (
      <span className="flex-shrink-0 px-1.5 py-0.5 rounded font-medium bg-amber-50 text-amber-800">
        {n} jobs
      </span>
    )
  }
  return (
    <span className="flex-shrink-0 px-1.5 py-0.5 rounded font-medium bg-gray-100 text-gray-600">
      {n} job{n === 1 ? '' : 's'}
    </span>
  )
}

function DraggableTrayItem({
  id,
  label,
  count,
  dragData,
  isSubcontractor = false,
}: {
  id: string
  label: string
  count: number
  dragData: DragPayload
  isSubcontractor?: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    data: dragData,
  })
  const style = transform
    ? { transform: CSS.Translate.toString(transform), opacity: isDragging ? 0.4 : 1 }
    : undefined

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between text-xs gap-2 rounded-lg px-2 py-2 min-h-[44px] ${
        isDragging ? 'bg-blue-50' : 'hover:bg-gray-50'
      }`}
    >
      <button
        type="button"
        className="flex items-center gap-1.5 min-w-0 min-h-[40px] text-left cursor-grab active:cursor-grabbing touch-none"
        {...listeners}
        {...attributes}
      >
        <GripVerticalIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
        <span className="text-gray-800 truncate">{label}</span>
        {isSubcontractor && (
          <span className="flex-shrink-0 px-1 py-0.5 text-[9px] leading-none rounded bg-orange-100 text-orange-800">
            Sub
          </span>
        )}
      </button>
      <span className="flex items-center gap-1.5 flex-shrink-0">
        {workloadBar(count)}
        {capacityBadge(count)}
      </span>
    </li>
  )
}

function DroppableJobCard({
  job,
  needsPeople,
  isOver,
  children,
}: {
  job: DispatchJob
  needsPeople: boolean
  isOver: boolean
  children: React.ReactNode
}) {
  const { setNodeRef, isOver: dropIsOver } = useDroppable({
    id: `job:${job.id}`,
    data: { jobId: job.id },
  })
  const highlight = isOver || dropIsOver

  return (
    <div
      ref={setNodeRef}
      className={`rounded-lg border p-4 transition-colors ${
        highlight
          ? 'border-blue-400 bg-blue-50/60 ring-2 ring-blue-200'
          : needsPeople
            ? 'border-amber-200 bg-amber-50/30'
            : 'border-gray-200 bg-white'
      }`}
    >
      {children}
    </div>
  )
}

export function DispatchBoard({
  jobs,
  jobAssignments,
  crews,
  employees,
  crewJobCounts,
  employeeJobCounts,
  assigningJobId,
  savingAssignment,
  getStatusColor,
  formatTime,
  onToggleEdit,
  onAssignCrew,
  onAssignEmployee,
  onRemove,
  onViewJob,
  onAutoAssignByProximity,
  autoAssigning = false,
}: Props) {
  const [filter, setFilter] = useState<'all' | 'needs'>('all')
  const [activeDrag, setActiveDrag] = useState<DragPayload | null>(null)
  const [overJobId, setOverJobId] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  )

  const needsPeople = useMemo(
    () => jobs.filter((j) => !jobHasAssigneeCoverage(j, jobAssignments[j.id])),
    [jobs, jobAssignments]
  )
  const covered = useMemo(
    () => jobs.filter((j) => jobHasAssigneeCoverage(j, jobAssignments[j.id])),
    [jobs, jobAssignments]
  )

  const sortByTime = (a: DispatchJob, b: DispatchJob) =>
    (a.scheduled_start_time || '').localeCompare(b.scheduled_start_time || '')

  const needsSorted = useMemo(() => needsPeople.slice().sort(sortByTime), [needsPeople])
  const coveredSorted = useMemo(() => covered.slice().sort(sortByTime), [covered])

  const peopleByLoad = useMemo(
    () =>
      employees
        .slice()
        .sort((a, b) => (employeeJobCounts[a.id] || 0) - (employeeJobCounts[b.id] || 0)),
    [employees, employeeJobCounts]
  )
  const crewsByLoad = useMemo(
    () =>
      crews
        .slice()
        .sort((a, b) => (crewJobCounts[a.id] || 0) - (crewJobCounts[b.id] || 0)),
    [crews, crewJobCounts]
  )

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as DragPayload | undefined
    if (data) setActiveDrag(data)
  }

  const handleDragOver = (event: DragOverEvent) => {
    const overId = event.over?.id
    if (typeof overId === 'string' && overId.startsWith('job:')) {
      setOverJobId(overId.slice(4))
    } else {
      setOverJobId(null)
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const data = event.active.data.current as DragPayload | undefined
    const overId = event.over?.id
    setActiveDrag(null)
    setOverJobId(null)
    if (!data || typeof overId !== 'string' || !overId.startsWith('job:')) return
    const jobId = overId.slice(4)
    if (data.type === 'employee') onAssignEmployee(jobId, data.id)
    else onAssignCrew(jobId, data.id)
  }

  const renderJobCard = (job: DispatchJob) => {
    const assignments = jobAssignments[job.id] || []
    const empty = !jobHasAssigneeCoverage(job, assignments)
    return (
      <DroppableJobCard
        key={job.id}
        job={job}
        needsPeople={empty}
        isOver={overJobId === job.id}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-sm font-semibold text-gray-900 truncate">{job.title}</h4>
              <span className="text-xs text-gray-400">#{job.job_number}</span>
              <span
                className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(job.status)}`}
              >
                {job.status.replace('_', ' ')}
              </span>
              {empty && (
                <span className="text-[10px] font-medium text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded">
                  Unassigned
                </span>
              )}
            </div>
            <div className="flex flex-col gap-1 mt-1.5 text-xs text-gray-500 lg:flex-row lg:flex-wrap lg:items-center lg:gap-3">
              <span className="flex items-center gap-1">
                <ClockIcon className="w-3.5 h-3.5 flex-shrink-0" />
                {formatTime(job.scheduled_start_time || '')} –{' '}
                {formatTime(job.scheduled_end_time || '')}
              </span>
              <span className="flex items-center gap-1 truncate">
                <UserIcon className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{job.customerLabel}</span>
              </span>
              <span className="flex items-center gap-1 truncate">
                <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{job.propertyLabel}</span>
              </span>
            </div>
            {activeDrag && overJobId === job.id && (
              <p className="text-xs text-blue-700 font-medium mt-1.5">
                Drop to assign {activeDrag.label}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => onViewJob(job.id)}
            className="flex-shrink-0 min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-md"
            title="View details"
          >
            <EyeIcon className="w-4 h-4" />
          </button>
        </div>
        <DayJobAssignPanel
          assignments={assignments}
          crews={crews}
          employees={employees}
          isEditing={assigningJobId === job.id}
          saving={savingAssignment}
          crewJobCounts={crewJobCounts}
          employeeJobCounts={employeeJobCounts}
          onToggleEdit={() => onToggleEdit(job.id)}
          onAssignCrew={(crewId) => onAssignCrew(job.id, crewId)}
          onAssignEmployee={(employeeId) => onAssignEmployee(job.id, employeeId)}
          onRemove={(assignmentId) => onRemove(assignmentId, job.id)}
        />
      </DroppableJobCard>
    )
  }

  const teamPanel = (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
          <UsersIcon className="w-4 h-4" /> People today
        </h3>
        <p className="text-[11px] text-gray-500 mb-2">Drag onto a job to assign</p>
        {peopleByLoad.length === 0 ? (
          <p className="text-xs text-gray-400 italic">No active employees.</p>
        ) : (
          <ul className="space-y-0.5 max-h-72 overflow-y-auto">
            {peopleByLoad.map((emp) => {
              const n = employeeJobCounts[emp.id] || 0
              const label = formatEmployeeLabel(emp)
              return (
                <DraggableTrayItem
                  key={emp.id}
                  id={`employee:${emp.id}`}
                  label={label}
                  count={n}
                  dragData={{ type: 'employee', id: emp.id, label }}
                />
              )
            })}
          </ul>
        )}
      </div>
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-1">Crews today</h3>
        <p className="text-[11px] text-gray-500 mb-2">Drag onto a job to assign</p>
        {crewsByLoad.length === 0 ? (
          <p className="text-xs text-gray-400 italic">No active crews.</p>
        ) : (
          <ul className="space-y-0.5 max-h-48 overflow-y-auto">
            {crewsByLoad.map((c) => {
              const n = crewJobCounts[c.id] || 0
              return (
                <DraggableTrayItem
                  key={c.id}
                  id={`crew:${c.id}`}
                  label={c.name}
                  count={n}
                  dragData={{ type: 'crew', id: c.id, label: c.name }}
                  isSubcontractor={c.is_subcontractor === true}
                />
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={() => {
        setActiveDrag(null)
        setOverJobId(null)
      }}
    >
      <div className="space-y-4">
        {/* Coverage bar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <span className="font-semibold text-gray-900">{jobs.length} Jobs</span>
            <span className="text-gray-300">·</span>
            <span
              className={
                needsPeople.length > 0
                  ? 'font-medium text-amber-800'
                  : 'font-medium text-green-700'
              }
            >
              {needsPeople.length} Unassigned
            </span>
            <span className="text-gray-300">·</span>
            <span className="font-medium text-green-700">{covered.length} Covered</span>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap sm:justify-end">
            {onAutoAssignByProximity && (
              <button
                type="button"
                onClick={onAutoAssignByProximity}
                disabled={autoAssigning || needsPeople.length === 0 || employees.length === 0}
                className="flex items-center gap-1.5 min-h-[44px] px-3 py-2 text-xs font-medium rounded-xl bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50"
                title="Assign unassigned jobs to the closest employee by last known location"
              >
                <NavigationIcon className="w-3.5 h-3.5" />
                <span className="sm:hidden">{autoAssigning ? 'Assigning…' : 'Auto-assign'}</span>
                <span className="hidden sm:inline">
                  {autoAssigning ? 'Assigning…' : 'Auto-assign by nearest location'}
                </span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setFilter('needs')}
              className={`min-h-[44px] px-3 py-2 text-xs font-medium rounded-xl border transition-colors ${
                filter === 'needs'
                  ? 'bg-amber-100 border-amber-300 text-amber-900'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              Unassigned
            </button>
            <button
              type="button"
              onClick={() => setFilter('all')}
              className={`min-h-[44px] px-3 py-2 text-xs font-medium rounded-xl border transition-colors ${
                filter === 'all'
                  ? 'bg-blue-50 border-blue-300 text-blue-800'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              All Jobs
            </button>
          </div>
        </div>

        {/* Mobile team strip — horizontal snap scroll */}
        <div className="lg:hidden -mx-1 px-1 pb-1">
          <p className="text-[11px] text-gray-500 mb-1.5 px-1">Drag people onto jobs</p>
          <ul className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-1 [-webkit-overflow-scrolling:touch]">
            {peopleByLoad.map((emp) => {
              const n = employeeJobCounts[emp.id] || 0
              const label = formatEmployeeLabel(emp)
              return (
                <li
                  key={emp.id}
                  className="snap-start flex-shrink-0 min-w-[9.5rem] rounded-xl border border-gray-200 bg-white shadow-sm"
                >
                  <DraggableTrayItem
                    id={`employee-mobile:${emp.id}`}
                    label={label}
                    count={n}
                    dragData={{ type: 'employee', id: emp.id, label }}
                  />
                </li>
              )
            })}
            {crewsByLoad.map((c) => {
              const n = crewJobCounts[c.id] || 0
              return (
                <li
                  key={c.id}
                  className="snap-start flex-shrink-0 min-w-[9.5rem] rounded-xl border border-gray-200 bg-white shadow-sm"
                >
                  <DraggableTrayItem
                    id={`crew-mobile:${c.id}`}
                    label={c.name}
                    count={n}
                    dragData={{ type: 'crew', id: c.id, label: c.name }}
                    isSubcontractor={c.is_subcontractor === true}
                  />
                </li>
              )
            })}
          </ul>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            {jobs.length === 0 ? (
              <p className="text-sm text-gray-500 py-8 text-center">No jobs for this day.</p>
            ) : (
              <>
                {(filter === 'all' || filter === 'needs') && needsSorted.length > 0 && (
                  <section className="space-y-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-800 flex items-center gap-2">
                      Unassigned
                      <span className="normal-case font-medium bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded">
                        {needsSorted.length}
                      </span>
                    </h3>
                    <div className="space-y-3">{needsSorted.map(renderJobCard)}</div>
                  </section>
                )}

                {filter === 'all' && coveredSorted.length > 0 && (
                  <section className="space-y-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-2">
                      Covered
                      <span className="normal-case font-medium bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">
                        {coveredSorted.length}
                      </span>
                    </h3>
                    <div className="space-y-3">{coveredSorted.map(renderJobCard)}</div>
                  </section>
                )}

                {filter === 'needs' && needsSorted.length === 0 && (
                  <p className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-4 py-6 text-center">
                    All jobs for this day have someone assigned.
                  </p>
                )}
              </>
            )}
          </div>

          <div className="hidden lg:block">{teamPanel}</div>
        </div>
      </div>

      <DragOverlay>
        {activeDrag ? (
          <div className="rounded-md border border-blue-300 bg-white shadow-lg px-3 py-2 text-xs font-medium text-gray-900 flex items-center gap-2">
            <GripVerticalIcon className="w-3.5 h-3.5 text-blue-500" />
            Assign {activeDrag.label}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}
